import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import BrandNav from "../components/BrandNav";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 15;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", paid: false, tier_code: null, amount: null, currency: null });
  const attemptsRef = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "no_session", paid: false });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await fetch(`${API_BASE}/api/checkout/status/${encodeURIComponent(sessionId)}`);
        const body = await res.json();
        if (cancelled) return;
        if (body.payment_status === "paid") {
          setState({ status: "paid", paid: true, tier_code: body.tier_code, amount: body.amount, currency: body.currency });
          toast.success("Payment received — access unlocked.");
          return;
        }
        if (body.payment_status === "failed" || body.payment_status === "expired") {
          setState({ status: "failed", paid: false });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", paid: false });
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", paid: false });
          return;
        }
        setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <div className="ftg-verify">
          <div className="ftg-verify__card ftg-verify__card--valid">
            {state.status === "polling" && (
              <>
                <Loader2 size={36} className="ftg-spin" />
                <h1 className="ftg-verify__title" data-testid="checkout-polling">Confirming your payment&hellip;</h1>
                <p className="ftg-verify__body">This usually takes just a few seconds.</p>
              </>
            )}

            {state.status === "paid" && (
              <>
                <div className="ftg-celebrate__sparkles" style={{ justifyContent: "center" }}>
                  <Sparkles size={20} />
                  <Sparkles size={14} />
                  <Sparkles size={18} />
                </div>
                <CheckCircle2 size={44} className="ftg-verify__award" data-testid="checkout-paid" />
                <div className="ftg-verify__eyebrow">Payment received</div>
                <h1 className="ftg-verify__title">You&apos;re in.</h1>
                <p className="ftg-verify__lede">
                  Thank you for supporting FutureTec Global. Your enrollment has been upgraded and
                  the additional terms are unlocked on your dashboard.
                </p>
                {state.amount && (
                  <dl className="ftg-verify__meta" data-testid="checkout-amount-summary">
                    <div><dt>Amount</dt><dd>£{Number(state.amount).toFixed(2)}</dd></div>
                    <div><dt>Reference</dt><dd>{state.tier_code}</dd></div>
                  </dl>
                )}
                <Link to="/dashboard" className="ftg-btn ftg-btn--primary" data-testid="checkout-go-dashboard">
                  Go to my dashboard
                </Link>
              </>
            )}

            {state.status === "failed" && (
              <>
                <h1 className="ftg-verify__title" data-testid="checkout-failed">Payment didn&apos;t go through</h1>
                <p className="ftg-verify__body">No charge was made. You can try again from the pricing section.</p>
                <button type="button" onClick={() => nav("/#pricing")} className="ftg-btn ftg-btn--secondary">
                  Back to pricing
                </button>
              </>
            )}

            {state.status === "timeout" && (
              <>
                <h1 className="ftg-verify__title" data-testid="checkout-timeout">Still confirming&hellip;</h1>
                <p className="ftg-verify__body">
                  Stripe hasn&apos;t confirmed the payment yet. It usually finalises within a minute — refresh this page
                  or check the Payments tab on your dashboard.
                </p>
                <Link to="/dashboard" className="ftg-btn ftg-btn--ghost">Open dashboard</Link>
              </>
            )}

            {state.status === "no_session" && (
              <>
                <h1 className="ftg-verify__title">No session found</h1>
                <p className="ftg-verify__body">Please retry from the pricing section.</p>
                <Link to="/#pricing" className="ftg-btn ftg-btn--ghost">Back to pricing</Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
